/**
 * `round T-2`, `M5` (`P2-DLW-017`; WF-DLR-11): **the settlement as the dealer's door answers it**
 * (`GET /api/v1/me/settlements`), one row per car, read by the session and rendered by
 * `routes/settlement-card.tsx`. It lives beside the session so the card and the session share the
 * shape without the card importing a value from the session that imports the card (the
 * boundaries' `no-circular-modules`).
 */
export interface DealerSettlement {
  readonly id: string;
  readonly reference: string;
  readonly vehicleId: string;
  readonly payerKind: string;
  readonly salePriceAmount: number;
  readonly ratePct: number;
  readonly hakiliAmount: number;
  readonly dealerAmount: number;
  readonly status: string;
  readonly missing: string | null;
  readonly blockedReason: string | null;
  readonly hakiliLegConfirmedAt: string | null;
  readonly dealerLegConfirmedAt: string | null;
  readonly dealerLegReceivedOn: string | null;
  readonly dealerNonReceiptReportedAt: string | null;
  readonly clientName: string | null;
  /**
   * `DEALER V2 B-D1`, ARGENT: what the door serves beside the settlement for the sale card (`settlements.controller.ts:237-242`, `settlement.service.ts`): the car's label and stock reference, the dossier's reference
   * and the day it opened. OPTIONAL on purpose: the car page's specs build this type without them; the projection always sets them (`null` where the door serves none).
   */
  readonly vehicleLabel?: string | null;
  readonly stockReference?: string | null;
  readonly dossierReference?: string | null;
  readonly openedAt?: string | null;
  readonly voucher: {
    readonly reference: string;
    readonly issuedAt: string;
    readonly presentedAt: string | null;
  } | null;
}

const str = (value: unknown): string | null => (typeof value === "string" ? value : null);
const num = (value: unknown): number | null => (typeof value === "number" ? value : null);

export function dealerSettlementsFrom(answer: unknown): readonly DealerSettlement[] {
  const rows = (answer as { settlements?: unknown } | null)?.settlements;
  if (!Array.isArray(rows)) return [];
  const out: DealerSettlement[] = [];
  for (const raw of rows) {
    const one = raw as Record<string, unknown> | null;
    const id = str(one?.["id"]);
    const reference = str(one?.["reference"]);
    const vehicleId = str(one?.["vehicleId"]);
    const salePriceAmount = num(one?.["salePriceAmount"]);
    const ratePct = num(one?.["ratePct"]);
    const hakiliAmount = num(one?.["hakiliAmount"]);
    const dealerAmount = num(one?.["dealerAmount"]);
    const status = str(one?.["status"]);
    if (
      one === null ||
      id === null ||
      reference === null ||
      vehicleId === null ||
      salePriceAmount === null ||
      ratePct === null ||
      hakiliAmount === null ||
      dealerAmount === null ||
      status === null
    ) {
      continue;
    }
    const voucher = one["voucher"] as Record<string, unknown> | null;
    const voucherReference = str(voucher?.["reference"]);
    const voucherIssuedAt = str(voucher?.["issuedAt"]);
    out.push({
      id,
      reference,
      vehicleId,
      payerKind: str(one["payerKind"]) ?? "banque",
      salePriceAmount,
      ratePct,
      hakiliAmount,
      dealerAmount,
      status,
      missing: str(one["missing"]),
      blockedReason: str(one["blockedReason"]),
      hakiliLegConfirmedAt: str(one["hakiliLegConfirmedAt"]),
      dealerLegConfirmedAt: str(one["dealerLegConfirmedAt"]),
      dealerLegReceivedOn: str(one["dealerLegReceivedOn"]),
      dealerNonReceiptReportedAt: str(one["dealerNonReceiptReportedAt"]),
      clientName: str(one["clientName"]),
      vehicleLabel: str(one["vehicleLabel"]),
      stockReference: str(one["stockReference"]),
      dossierReference: str(one["dossierReference"]),
      openedAt: str(one["openedAt"]),
      voucher:
        voucherReference === null || voucherIssuedAt === null
          ? null
          : {
              reference: voucherReference,
              issuedAt: voucherIssuedAt,
              presentedAt: str(voucher?.["presentedAt"]),
            },
    });
  }
  return out;
}
